<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { apiGetPreviewUrl } from '@ilteducation/content';
import ContentHoverCard from '../ContentHoverCard.vue';
import ContentCoverStack from '../ContentCoverStack.vue';
import useCoverDimensions from '../helpers/use-cover-dimensions';
import type { CoverFeatureItem } from '../CoverFeatures.vue';
import dummyImageUrl from '../assets/materials/dummy-cover.png';

const maxWidth = 180;
const maxHeight = 180;
const { coverRef, coverHeight, coverWidth, onImageLoaded } = useCoverDimensions(120, maxHeight);

type Props = {
  material: MaterialV1;
  duplicatesCount?: number;
  hoverOverflowHidden?: boolean;
  rounded?: boolean;
  hoverRoundedClass?: string;
};

const props = withDefaults(defineProps<Props>(), {
  duplicatesCount: 0,
  rounded: true,
  hoverRoundedClass: 'rounded-lg', // On Polylino we use rounded-lg, in Manabua we use rounded-sm
  hoverOverflowHidden: true,
});
const emit = defineEmits<{
  (e: 'onMaterialClick', material: MaterialV1): void;
  (e: 'onFeatureClick', feature: CoverFeatureItem): void;
}>();
const { material, duplicatesCount } = toRefs(props);

const dummyImage = dummyImageUrl;
const previewUrl = apiGetPreviewUrl(material.value.id, 500); // define the image height for the preview from server;
const hasStack = computed(() => duplicatesCount.value > 1);

const placeHolder = ref(
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mO88R8AArUB2TtNwp8AAAAASUVORK5CYII=',
);
</script>

<template>
  <ContentHoverCard
    :content-card-width="coverWidth"
    :content-card-height="maxHeight"
    :image-height="coverHeight"
    :has-stack="hasStack"
    :data-material-id="material.id"
    :overflow-hidden="hoverOverflowHidden"
    :is-rounded="rounded"
    :hover-rounded-class="hoverRoundedClass"
    @on-card-click="emit('onMaterialClick', material)"
  >
    <template #coverFeatures>
      <slot name="coverFeatures"></slot>
    </template>
    <template #image>
      <v-img
        ref="coverRef"
        class="material-cover bg-surface-container-low elevation-4"
        :width="coverWidth"
        :height="coverHeight"
        :src="previewUrl"
        :lazy-src="placeHolder"
        :alt="material.title"
        cover
        tabindex="0"
        @load="onImageLoaded(maxWidth, maxHeight)"
      >
        <template #error>
          <v-img height="100%" :src="dummyImage" :alt="material.title" cover />
        </template>
      </v-img>
    </template>
    <template v-if="hasStack && coverWidth && coverHeight" #stack>
      <ContentCoverStack
        :width="coverWidth"
        :height="coverHeight"
        class="material-cover-stack"
        :src="previewUrl"
        :level="duplicatesCount"
        :alt="material.title"
      >
        <template #error>
          <v-img height="100%" :src="dummyImage" :alt="material.title" cover />
        </template>
      </ContentCoverStack>
    </template>
    <template #title>
      <div class="mt-2">
        <p class="text-2 material-ttl">{{ material.title }}</p>
      </div>
    </template>
  </ContentHoverCard>
</template>

<style scoped lang="scss">
.material {
  cursor: pointer;
  outline: none !important;
  border: none !important;
  &-ttl {
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}
.text-on-surface-medium {
  color: rgb(var(--v-theme-on-surface-medium));
}
:deep(.v-card-item__content) {
  align-self: end;
}
</style>
